import React, { useEffect, useMemo, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { API } from "@/lib/brand";
import { ArrowLeft, CurrencyGbp, ChartBar, Sparkle } from "@phosphor-icons/react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";

// /admin/ai-cost — Iter40 owner-only dashboard showing daily Emergent
// Universal Key spend broken down by regen count vs estimated cost. Reads
// from GET /api/admin/ai-cost, which aggregates fs_regen_log.
export default function AdminAiCostPage() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Iter40 code-review fix: don't fire the admin API on non-admin users.
    // The <Navigate> gate below runs AFTER the effect, so without this
    // early-return a non-admin would flash a 403 error toast on redirect.
    if (!user?.is_admin) return;
    let cancelled = false;
    setLoading(true);
    axios
      .get(`${API}/admin/ai-cost?days=${days}`)
      .then((res) => {
        if (!cancelled) setData(res.data);
      })
      .catch((e) => {
        toast.error(e?.response?.data?.detail || "Could not load AI cost data");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [days, user]);

  const chartData = useMemo(() => {
    if (!data?.daily) return [];
    return data.daily.map((d) => ({
      label: d.date.slice(5), // MM-DD
      regens: d.regens,
      cost: Number(d.estimated_cost_usd) || 0,
    }));
  }, [data]);

  if (!user) return null;
  if (!user.is_admin) return <Navigate to="/dashboard" replace />;

  return (
    <div className="space-y-8" data-testid="admin-ai-cost-page">
      <div className="flex items-center gap-3">
        <button
          type="button"
          className="btn-secondary"
          onClick={() => nav("/admin")}
          data-testid="ai-cost-back-btn"
        >
          <ArrowLeft size={14} weight="bold" /> Back to Admin
        </button>
      </div>

      <div>
        <div className="section-eyebrow flex items-center gap-2">
          <Sparkle size={14} weight="duotone" /> AI cost panel
        </div>
        <h1 className="font-heading uppercase text-4xl md:text-5xl tracking-wide mt-4 leading-[1.05]">
          Where the LLM budget's going.
        </h1>
        <p className="mt-3 text-black/70 leading-relaxed max-w-2xl">
          Daily Forecast Story regen counts + rough cost estimate based on Claude Sonnet 4.5
          pricing. Real spend lives in your Emergent dashboard — this view is directional so
          you can spot spikes without leaving forecastly.
        </p>
      </div>

      {/* Window picker */}
      <div className="flex flex-wrap gap-2" data-testid="ai-cost-window">
        {[7, 14, 30, 90].map((n) => (
          <button
            key={n}
            type="button"
            onClick={() => setDays(n)}
            data-testid={`ai-cost-window-${n}`}
            className={`px-4 py-2 border-[1.5px] border-black rounded-xl font-heading uppercase text-sm tracking-wide transition-all ${
              days === n ? "bg-[#FFBD4A]" : "bg-white"
            }`}
          >
            Last {n} days
          </button>
        ))}
      </div>

      {loading && (
        <div className="card" data-testid="ai-cost-loading">
          Reading the ledger…
        </div>
      )}

      {!loading && data && (
        <>
          {/* Totals strip */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="ai-cost-totals">
            <StatBox
              label="Total regens"
              value={data.total_regens.toLocaleString("en-GB")}
              icon={<ChartBar size={18} weight="duotone" />}
            />
            <StatBox
              label="Estimated spend (USD)"
              value={`$${data.total_estimated_cost_usd.toFixed(2)}`}
              accent
              icon={<CurrencyGbp size={18} weight="duotone" />}
            />
            <StatBox
              label="Window"
              value={`${data.window_days} days`}
              icon={<ChartBar size={18} weight="duotone" />}
            />
          </div>

          <p className="text-xs text-black/55 leading-relaxed" data-testid="ai-cost-note">
            {data.pricing_note}
          </p>

          {/* Daily chart */}
          <div className="card" data-testid="ai-cost-daily-chart">
            <h3 className="font-heading uppercase text-xl">Daily regens</h3>
            <div className="mt-4 w-full h-[260px] min-h-[260px]" style={{ minHeight: 260 }}>
              <ResponsiveContainer width="100%" height="100%" minHeight={240} debounce={50}>
                <BarChart data={chartData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#00000010" />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                  <Tooltip
                    formatter={(val, name) => (name === "cost" ? [`$${val.toFixed(3)}`, "Cost"] : [val, "Regens"])}
                    contentStyle={{ borderRadius: 10, border: "1.5px solid #000" }}
                  />
                  <Bar dataKey="regens" fill="#FFBD4A" stroke="#000" strokeWidth={1.5} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Trigger breakdown */}
          {data.by_trigger?.length > 0 && (
            <div className="card" data-testid="ai-cost-triggers">
              <h3 className="font-heading uppercase text-xl">What triggered the regens</h3>
              <div className="mt-4 grid sm:grid-cols-2 md:grid-cols-3 gap-3">
                {data.by_trigger.map((t) => (
                  <div
                    key={t.trigger}
                    className="border-[1.5px] border-black rounded-xl p-3"
                    data-testid={`ai-cost-trigger-${t.trigger}`}
                  >
                    <div className="text-xs uppercase tracking-wider text-black/60">
                      {t.trigger.replace(/_/g, " ")}
                    </div>
                    <div className="font-heading text-2xl mt-1">{t.regens}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Per-user leaderboard */}
          <div className="card" data-testid="ai-cost-leaderboard">
            <h3 className="font-heading uppercase text-xl">Top users by regens</h3>
            {data.leaderboard.length === 0 ? (
              <p className="mt-3 text-black/60">No regens in this window yet.</p>
            ) : (
              <div className="mt-4 overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="border-b-[1.5px] border-black text-left">
                      <th className="py-2 pr-4 font-heading uppercase text-xs tracking-wider">Email</th>
                      <th className="py-2 pr-4 font-heading uppercase text-xs tracking-wider">Regens</th>
                      <th className="py-2 pr-4 font-heading uppercase text-xs tracking-wider">Est. cost (USD)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.leaderboard.map((row) => (
                      <tr
                        key={row.user_id}
                        className="border-b border-black/10"
                        data-testid={`ai-cost-row-${row.user_id}`}
                      >
                        <td className="py-2 pr-4">{row.email}</td>
                        <td className="py-2 pr-4">{row.regens}</td>
                        <td className="py-2 pr-4">${row.estimated_cost_usd.toFixed(3)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function StatBox({ label, value, accent, icon }) {
  return (
    <div
      className={`border-[1.5px] border-black rounded-xl p-4 ${
        accent ? "bg-[#FFBD4A]" : "bg-white"
      }`}
    >
      <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-black/60">
        {icon}
        {label}
      </div>
      <div className="font-heading text-3xl mt-1">{value}</div>
    </div>
  );
}
